/**
 * BuildScenarioStoreStyles.js - Color prioritization schema for scenarios
 *
 * This module defines the color scheme used for scenario visualization
 * in the build scenario functionality.
 */

/**
 * Color prioritization schema for scenarios
 * Maps scenario IDs (1-10) to specific colors for consistent visualization
 *
 * Colors are chosen to be:
 * - Visually distinct from each other
 * - Accessible for colorblind users
 * - Professional and pleasant to look at
 */
export const SCENARIO_COLOR_SCHEMA = {
  1: '#B2DF8A', // Light green
  2: '#1F78B4', // Blue
  3: '#FB9A99', // Light pink
  4: '#E31A1C', // Red
  5: '#FDBF6F', // Light orange
  6: '#FF7F00', // Orange
  7: '#CAB2D6', // Light purple
  8: '#6A3D9A', // Purple
  9: '#FFFF99', // Light yellow
  10: '#B15928', // Brown
}

/**
 * Special colors for non-scenario data
 */
export const SPECIAL_COLORS = {
  CLEAN_BASELINE: '#A9D9E9', // Ice blue for original model prediction
  DEFAULT: '#666666', // Gray fallback color
}

/**
 * Gets the color for a specific scenario ID
 * Falls back to a default color if the scenario ID is not in the schema
 *
 * @param {number} scenarioId - The ID of the scenario (1-10)
 * @returns {string} Hex color code for the scenario
 */
export const getScenarioColor = (scenarioId) => {
  return SCENARIO_COLOR_SCHEMA[scenarioId] || SPECIAL_COLORS.DEFAULT
}

/**
 * Gets all available scenario colors as an array
 * Useful for color pickers or previews
 *
 * @returns {Array<string>} Array of hex color codes
 */
export const getAllScenarioColors = () => {
  return Object.values(SCENARIO_COLOR_SCHEMA)
}

/**
 * Gets the next available color for a new scenario
 * Cycles through the color schema based on the scenario count
 *
 * @param {number} scenarioCount - Current number of scenarios
 * @returns {string} Hex color code for the next scenario
 */
export const getNextScenarioColor = (scenarioCount) => {
  const colorIndex = ((scenarioCount - 1) % 10) + 1 // Cycle through 1-10
  return SCENARIO_COLOR_SCHEMA[colorIndex]
}

/**
 * Validates if a color is a valid hex color code
 *
 * @param {string} color - Color string to validate
 * @returns {boolean} True if valid hex color
 */
export const isValidHexColor = (color) => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)
}

export default {
  SCENARIO_COLOR_SCHEMA,
  SPECIAL_COLORS,
  getScenarioColor,
  getAllScenarioColors,
  getNextScenarioColor,
  isValidHexColor,
}
